import { Draft } from 'immer';

import { CreditAgreement } from '@finley-technologies/finley-types';

import { BaseFormFields } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/facility-mechanics/FacilityMechanics';
import {
  createCADateProperty,
  createCAPercentageRateProperty,
  createCAStructureProperty,
  createCATextProperty,
} from '~/core/routes/settings/implementations/common/creditAgreementUtils';

type CreditAgreementFullArtifact = CreditAgreement.DomainEntity.CreditAgreementFullArtifact;

// This function receives a 'draft' of the Credit Agreement, which is provided by the immer library.
// This means that it is ok to mutate the `draft` variable - this produces a new CA object, and leaves
// the original CA reference untouched.
export const setFormValues = (
  draft: Draft<CreditAgreementFullArtifact>,
  values: BaseFormFields,
) => {
  const { facilityMechanics: fm } = draft;

  // Required fields
  if (values.closingDate) {
    fm.closingDate = createCADateProperty(
      values.closingDate,
      'Closing Date',
      fm.closingDate,
    );
  }

  if (values.finalMaturityDate) {
    fm.finalMaturityDate = createCADateProperty(
      values.finalMaturityDate,
      'Maturity Date',
      fm.finalMaturityDate,
    );

    // Update description if it exists in the form values
    if (values.maturityDateDescription !== undefined) {
      fm.finalMaturityDate.description = values.maturityDateDescription;
    }
  }

  // This property will default to 'fixedProportion'. There is no user input for this value. See:
  // https://linear.app/finleycms/issue/ENG-16751/facility-mechanics-default-to-showing-only-fixed-proportion-and-not
  fm.structure = createCAStructureProperty(
    'fixedProportion',
    'Structure',
    fm.structure,
  );

  if (draft.loanMechanicsV1) {
    draft.loanMechanicsV1.isTermLoan = values.termLoan === 'true';
  }

  // Optional fields
  if (values.amortizationPeriodStart) {
    fm.amortizationPeriodStart = createCADateProperty(
      values.amortizationPeriodStart,
      'Amortization Period Start',
      fm.amortizationPeriodStart,
    );
  } else {
    delete fm.amortizationPeriodStart;
  }

  if (values.callProtectionExpirationDate) {
    fm.callProtectionExpirationDate = createCADateProperty(
      values.callProtectionExpirationDate,
      'Call Protection Expiration',
      fm.callProtectionExpirationDate,
    );
  } else {
    delete fm.callProtectionExpirationDate;
  }

  if (values.callProtectionValue) {
    fm.callProtectionValue = createCAPercentageRateProperty(
      values.callProtectionValue,
      'Call Protection Value',
      fm.callProtectionValue,
    );
  } else {
    delete fm.callProtectionValue;
  }

  if (values.governingLaw) {
    fm.governingLaw = createCATextProperty(
      values.governingLaw,
      'Governing Law',
      fm.governingLaw,
    );
  } else {
    delete fm.governingLaw;
  }

  if (values.minimumUtilization) {
    fm.minimumUtilization = createCAPercentageRateProperty(
      values.minimumUtilization,
      'Minimum Utilization',
      fm.minimumUtilization,
    );
  } else {
    delete fm.minimumUtilization;
  }

  if (values.yieldMaintenance) {
    fm.yieldMaintenance = createCADateProperty(
      values.yieldMaintenance,
      'Yield Maintenance',
      fm.yieldMaintenance,
    );
  } else {
    delete fm.yieldMaintenance;
  }
};
